import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import { AlertTriangle, TrendingUp, DollarSign, Activity, ShoppingCart, Zap } from 'lucide-react';
import api from '../api';
import { DashboardData } from '../types';

const BREED_LABELS: Record<string, string> = {
  qorabosh: 'Qorabosh', golshtin: 'Golshtin', simmental: 'Simmental',
  abuerdeen: 'Aberdeen', mahalliy: 'Mahalliy',
};
const PIE_COLORS = ['#0D7E6E', '#14A88A', '#E5831A', '#0F6E56', '#5DCAA5'];

export default function Dashboard({ onNavigate }: { onNavigate: (p: string, id?: number) => void }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getDashboard().then(r => { setData(r.data); setLoading(false); });
  }, []);

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!data) return null;

  const { stats, weight_trend, feed_trend, breed_distribution, sick_animals, sell_ready } = data;

  return (
    <div className="page-body">
      {/* Alerts */}
      {stats.sick_animals > 0 && (
        <div className="alert alert-red">
          <AlertTriangle size={16} />
          <span><strong>{stats.sick_animals} ta kasal chorva</strong> — darhol e'tibor talab qilinadi</span>
          <button className="btn btn-sm btn-danger" style={{ marginLeft: 'auto' }}
            onClick={() => onNavigate('animals', undefined)}>Ko'rish</button>
        </div>
      )}
      {stats.sell_ready_count > 0 && (
        <div className="alert alert-teal">
          <ShoppingCart size={16} />
          <span><strong>{stats.sell_ready_count} ta chorva</strong> sotishga tayyor!</span>
          <button className="btn btn-sm btn-outline" style={{ marginLeft: 'auto', background: 'transparent' }}
            onClick={() => onNavigate('animals')}>Ko'rish</button>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card teal">
          <div className="stat-label">Jami chorvalar</div>
          <div className="stat-value">{stats.total_animals}</div>
          <div className="stat-sub">{stats.active_animals} ta faol</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">O'rtacha vazn</div>
          <div className="stat-value">{stats.avg_weight} <span style={{ fontSize: 16 }}>kg</span></div>
          <div className="stat-sub">+{stats.avg_total_gain} kg o'sish</div>
        </div>
        <div className="stat-card amber">
          <div className="stat-label">Kunlik o'rtacha o'sish</div>
          <div className="stat-value">{stats.avg_daily_gain} <span style={{ fontSize: 14 }}>kg/kun</span></div>
          <div className="stat-sub">ADG ko'rsatkichi</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #E5831A' }}>
          <div className="stat-label">Bugungi yem xarajati</div>
          <div className="stat-value" style={{ fontSize: 20 }}>
            {stats.today_feed_cost > 0 ? `${(stats.today_feed_cost / 1000).toFixed(0)}K` : '—'}
            <span style={{ fontSize: 13 }}> so'm</span>
          </div>
          <div className="stat-sub">Barcha faol mollar</div>
        </div>
      </div>

      {/* Charts row 1 */}
      <div className="charts-grid">
        <div className="card">
          <div className="card-header">
            <h3>Vazn dinamikasi (6 oy)</h3>
            <TrendingUp size={16} style={{ color: 'var(--teal)' }} />
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={weight_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} domain={['auto', 'auto']} />
                <Tooltip formatter={(v: any) => [`${v} kg`, "O'rtacha vazn"]} />
                <Line type="monotone" dataKey="avg_weight" stroke="#0D7E6E"
                  strokeWidth={2.5} dot={{ fill: '#0D7E6E', r: 4 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Yem xarajati (6 oy)</h3>
            <DollarSign size={16} style={{ color: 'var(--amber)' }} />
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={feed_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(v: any) => [`${(v / 1000).toFixed(0)}K so'm`, 'Xarajat']} />
                <Bar dataKey="total_cost" fill="#E5831A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="charts-grid-3">
        <div className="card">
          <div className="card-header">
            <h3>Yem iste'moli (kg/oy)</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={feed_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v: any) => [`${v} kg`, 'Yem']} />
                <Bar dataKey="total_feed_kg" fill="#14A88A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Zot taqsimoti</h3></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={breed_distribution.map(b => ({ ...b, name: BREED_LABELS[b.breed] || b.breed }))}
                  dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  labelLine={false} fontSize={11}>
                  {breed_distribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="charts-grid">
        {/* Sell ready */}
        <div className="card">
          <div className="card-header">
            <h3>Sotishga tayyor</h3>
            <span className="badge badge-sell">{sell_ready.length} ta</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {sell_ready.length === 0
              ? <div className="empty-state"><p>Hozircha yo'q</p></div>
              : <table>
                <thead><tr><th>Tag</th><th>Hozirgi vazn</th><th>Maqsad</th></tr></thead>
                <tbody>
                  {sell_ready.map(a => (
                    <tr key={a.id} onClick={() => onNavigate('animal-detail', a.id)}>
                      <td><span className="tag-id">{a.tag_id}</span></td>
                      <td><strong style={{ color: 'var(--teal)' }}>{a.current_weight} kg</strong></td>
                      <td>{a.target_weight} kg</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </div>
        </div>

        {/* Sick animals */}
        <div className="card">
          <div className="card-header">
            <h3>Kasal chorvalar</h3>
            <span className="badge badge-kasal">{sick_animals.length} ta</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {sick_animals.length === 0
              ? <div className="empty-state"><p>Barcha chorva sog'lom 🎉</p></div>
              : <table>
                <thead><tr><th>Tag</th><th>Holat</th><th>Vazn</th></tr></thead>
                <tbody>
                  {sick_animals.map(a => (
                    <tr key={a.id} onClick={() => onNavigate('animal-detail', a.id)}>
                      <td><span className="tag-id">{a.tag_id}</span></td>
                      <td><span className={`badge badge-${a.status}`}>{a.status}</span></td>
                      <td>{a.latest_weight} kg</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </div>
        </div>
      </div>
    </div>
  );
}
